import {
  GENERATION_LAYOUT_EXHIBIT_EXPORT_ID,
  type GenerationLayoutExhibitFitComponent,
  type GenerationLayoutExhibitOverlayFeature,
  type GenerationLayoutExhibitSnapshot,
} from '../../types/generationImpact';
import { requireValidGenerationLayoutExhibitSnapshot } from './generationLayoutExhibit';
import { localizeLayoutExclusionRings } from './generationLayoutGeometry';

export const GENERATION_LAYOUT_EXHIBIT_SVG_WIDTH = 1600;
export const GENERATION_LAYOUT_EXHIBIT_SVG_HEIGHT = 1000;
export const GENERATION_LAYOUT_EXHIBIT_SVG_VIEW_BOX = '0 0 1600 1000' as const;

interface SvgPoint { x: number; y: number }

const COMPONENT_COLORS: Record<GenerationLayoutExhibitFitComponent['kind'], string> = {
  'generation-block': '#b45309',
  cooling: '#0369a1',
  switchyard: '#6d28d9',
  'ccs-capture': '#047857',
  'ccs-compression': '#0f766e',
  'natural-gas-route': '#c2410c',
  'co2-export-route': '#475569',
  'construction-laydown': '#a16207',
  'access-road': '#334155',
  road: '#334155',
  parking: '#64748b',
  building: '#9a3412',
  'data-center': '#be185d',
  generator: '#15803d',
  custom: '#be123c',
};

const CATEGORY_COLORS: Record<string, string> = {
  'concept.generation': '#b45309',
  'concept.ccs': '#047857',
  'impact.noise': '#9333ea',
  'impact.lighting': '#ca8a04',
  'impact.water': '#0284c7',
  'impact.air': '#475569',
  'infrastructure.pipeline': '#c2410c',
  'infrastructure.electrical': '#7c3aed',
  'infrastructure.transport': '#334155',
  'constraint.environmental': '#15803d',
};

function escapeXml(value: unknown): string {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&apos;');
}

function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

function decimal(value: number | null, digits = 1): string {
  if (value == null || !Number.isFinite(value)) return 'Not established';
  const [whole, fraction] = value.toFixed(digits).split('.');
  const grouped = whole!.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return fraction == null ? grouped : `${grouped}.${fraction}`;
}

function signedDecimal(value: number | null, digits = 1): string {
  const formatted = decimal(value, digits);
  return value != null && Number.isFinite(value) && value > 0 ? `+${formatted}` : formatted;
}

function compact(value: number): string {
  return Number(value.toFixed(3)).toString();
}

function ellipsize(value: string, maxLength: number): string {
  return value.length <= maxLength ? value : `${value.slice(0, Math.max(0, maxLength - 1))}…`;
}

function safeColor(feature: GenerationLayoutExhibitOverlayFeature): string {
  if (feature.color && /^#[a-f\d]{3,8}$/i.test(feature.color)) return feature.color;
  return CATEGORY_COLORS[feature.category] ?? '#475569';
}

function path(points: readonly SvgPoint[], close = false): string {
  if (points.length === 0) return '';
  return `${points.map((point, index) => (
    `${index === 0 ? 'M' : 'L'}${compact(point.x)},${compact(point.y)}`
  )).join(' ')}${close ? ' Z' : ''}`;
}

function localProjector(snapshot: GenerationLayoutExhibitSnapshot): (point: { xM: number; yM: number }) => SvgPoint {
  const panel = { x: 56, y: 154, width: 708, height: 442 };
  const fallbackWidth = snapshot.fit.plan?.usableEnvelope.widthM ?? 1;
  const fallbackHeight = snapshot.fit.plan?.usableEnvelope.heightM ?? 1;
  const baseWidth = Math.max(1, snapshot.fit.envelope?.widthM ?? fallbackWidth);
  const baseHeight = Math.max(1, snapshot.fit.envelope?.heightM ?? fallbackHeight);
  const rectangleCorners = (widthM: number, heightM: number) => [
    { xM: -widthM / 2, yM: -heightM / 2 },
    { xM: widthM / 2, yM: -heightM / 2 },
    { xM: widthM / 2, yM: heightM / 2 },
    { xM: -widthM / 2, yM: heightM / 2 },
  ];
  const points = [
    ...(snapshot.fit.envelope?.parcelPolygonM ?? rectangleCorners(baseWidth, baseHeight)),
    ...snapshot.fit.components.flatMap(component => component.polygonM),
    ...(snapshot.fit.plan
      ? rectangleCorners(
          snapshot.fit.plan.requiredEnvelope.widthM,
          snapshot.fit.plan.requiredEnvelope.heightM,
        )
      : []),
  ];
  const minX = Math.min(...points.map(point => point.xM));
  const maxX = Math.max(...points.map(point => point.xM));
  const minY = Math.min(...points.map(point => point.yM));
  const maxY = Math.max(...points.map(point => point.yM));
  const widthM = Math.max(1, maxX - minX);
  const heightM = Math.max(1, maxY - minY);
  const scale = Math.min(panel.width / (widthM * 1.06), panel.height / (heightM * 1.06));
  const localCenterX = (minX + maxX) / 2;
  const localCenterY = (minY + maxY) / 2;
  const centerX = panel.x + panel.width / 2;
  const centerY = panel.y + panel.height / 2;
  return point => ({
    x: centerX + (point.xM - localCenterX) * scale,
    y: centerY - (point.yM - localCenterY) * scale,
  });
}

function mapViewportPoints(snapshot: GenerationLayoutExhibitSnapshot): Array<{ lat: number; lng: number }> {
  const parcel = snapshot.fit.parcel?.polygon ?? [];
  const components = snapshot.fit.components.flatMap(component => component.coordinates);
  if (parcel.length >= 3) {
    // Keep the engineering test fit legible even when a source drawing carries
    // remote alignments. Every overlay path is still emitted and clip-masked;
    // only the viewport basis changes.
    return [...parcel, ...components];
  }
  return [
    ...components,
    ...snapshot.overlays.features.flatMap(feature => feature.geometry.coordinates),
  ];
}

function mapProjector(snapshot: GenerationLayoutExhibitSnapshot): (point: { lat: number; lng: number }) => SvgPoint {
  const panel = { x: 836, y: 154, width: 708, height: 442 };
  const points = mapViewportPoints(snapshot);
  const fallback = snapshot.fit.anchor;
  const referenceLat = points.length > 0
    ? (Math.min(...points.map(point => point.lat)) + Math.max(...points.map(point => point.lat))) / 2
    : fallback.lat;
  const referenceLng = points.length > 0
    ? (Math.min(...points.map(point => point.lng)) + Math.max(...points.map(point => point.lng))) / 2
    : fallback.lng;
  const earthRadiusM = 6_378_137;
  const radians = Math.PI / 180;
  const cosLatitude = Math.max(1e-8, Math.abs(Math.cos(referenceLat * radians)));
  const local = (point: { lat: number; lng: number }) => ({
    eastM: (point.lng - referenceLng) * radians * earthRadiusM * cosLatitude,
    northM: (point.lat - referenceLat) * radians * earthRadiusM,
  });
  const localPoints = points.length > 0 ? points.map(local) : [{ eastM: -1, northM: -1 }, { eastM: 1, northM: 1 }];
  const minEast = Math.min(...localPoints.map(point => point.eastM));
  const maxEast = Math.max(...localPoints.map(point => point.eastM));
  const minNorth = Math.min(...localPoints.map(point => point.northM));
  const maxNorth = Math.max(...localPoints.map(point => point.northM));
  const eastSpanM = Math.max(1, maxEast - minEast);
  const northSpanM = Math.max(1, maxNorth - minNorth);
  const scale = Math.min(panel.width / (eastSpanM * 1.1), panel.height / (northSpanM * 1.1));
  const localCenterEast = (minEast + maxEast) / 2;
  const localCenterNorth = (minNorth + maxNorth) / 2;
  const panelCenterX = panel.x + panel.width / 2;
  const panelCenterY = panel.y + panel.height / 2;
  return point => {
    const projected = local(point);
    return {
      x: panelCenterX + (projected.eastM - localCenterEast) * scale,
      y: panelCenterY - (projected.northM - localCenterNorth) * scale,
    };
  };
}

/** One-line buildable-area / flood-screening disclosure under the plan. */
function exclusionSummaryLine(snapshot: GenerationLayoutExhibitSnapshot): string {
  const exclusionSet = snapshot.fit.layout.exclusions;
  const usableSummary = snapshot.fit.usable;
  if (!exclusionSet && !usableSummary) return '';
  if (exclusionSet?.femaStatus === 'partial') {
    // One non-wrapping line in the 764 px left column: a fixed short clause
    // names a geometry fallback whenever the screening discloses one. The tier
    // on the capped coverage is the structural signal; the reason wording is
    // only a fallback for stored snapshots taken before the tier existed.
    const fallback = !!exclusionSet.femaCoverage?.geometryTier
      || /clipping failure|coordinate grid|single sweep|geometry fallback/i.test(exclusionSet.femaUnavailableReason ?? '');
    return fallback
      ? '<text x="56" y="597" fill="#9a3412" font-size="12" font-weight="700">FEMA screening incomplete (disclosed geometry fallback, fit not fully applied); remaining land not confirmed flood-free.</text>'
      : '<text x="56" y="597" fill="#9a3412" font-size="12" font-weight="700">FEMA screening incomplete; known exclusions retained, remaining land not confirmed flood-free.</text>';
  }
  if (exclusionSet?.femaStatus === 'unavailable') {
    return `<text x="56" y="597" fill="#9a3412" font-size="12" font-weight="700">FEMA flood screening unavailable${exclusionSet.femaUnavailableReason ? escapeXml(` — ${exclusionSet.femaUnavailableReason}`) : ''}; buildable area is not flood-screened.</text>`;
  }
  if (exclusionSet?.nwiStatus && exclusionSet.nwiStatus !== 'applied') {
    return '<text x="56" y="597" fill="#9a3412" font-size="12" font-weight="700">NWI mapping screen incomplete; remaining land is not confirmed wetlands-free.</text>';
  }
  if (!usableSummary) return '';
  const overlap = snapshot.fit.exclusionOverlapAcres;
  const parts = [
    `No-build exclusions ${decimal(usableSummary.excludedAcres, 1)} ac`,
    `usable ${decimal(usableSummary.usableAcres, 1)} of ${decimal(usableSummary.parcelAcres, 1)} ac`,
    overlap != null && overlap > 0.01
      ? `components on excluded ground ${decimal(overlap, 1)} ac`
      : 'components clear of exclusions',
  ];
  return `<text x="56" y="597" fill="#475569" font-size="12">${escapeXml(parts.join(' · '))}</text>`;
}

function renderPlan(snapshot: GenerationLayoutExhibitSnapshot): string {
  const project = localProjector(snapshot);
  const usable = snapshot.fit.plan?.usableEnvelope;
  const packing = snapshot.fit.plan?.packingEnvelope;
  const envelopeWidth = snapshot.fit.envelope?.widthM ?? usable?.widthM ?? 1;
  const envelopeHeight = snapshot.fit.envelope?.heightM ?? usable?.heightM ?? 1;
  const envelope = [
    { xM: -envelopeWidth / 2, yM: -envelopeHeight / 2 },
    { xM: envelopeWidth / 2, yM: -envelopeHeight / 2 },
    { xM: envelopeWidth / 2, yM: envelopeHeight / 2 },
    { xM: -envelopeWidth / 2, yM: envelopeHeight / 2 },
  ];
  const parcel = snapshot.fit.envelope?.parcelPolygonM ?? envelope;
  const packingPolygon = packing ? [
    { xM: -packing.widthM / 2, yM: -packing.heightM / 2 },
    { xM: packing.widthM / 2, yM: -packing.heightM / 2 },
    { xM: packing.widthM / 2, yM: packing.heightM / 2 },
    { xM: -packing.widthM / 2, yM: packing.heightM / 2 },
  ] : [];
  const exclusionRings = snapshot.fit.parcel && snapshot.fit.envelope
    && snapshot.fit.layout.exclusions?.zones.length
    ? localizeLayoutExclusionRings(
        snapshot.fit.layout.exclusions.zones,
        { centroid: snapshot.fit.parcel.centroid },
        snapshot.fit.envelope,
        snapshot.fit.layout.rotationDeg,
      )
    : [];
  const exclusions = exclusionRings.map((ring, index) => (
    `<path d="${path(ring.map(project), true)}" fill="#dc2626" fill-opacity="0.18" stroke="#dc2626" stroke-width="1.5" stroke-dasharray="6 4" data-exclusion-index="${index}"><title>No-build exclusion zone</title></path>`
  )).join('');
  const components = snapshot.fit.components.map(component => {
    const labelPoint = project({ xM: component.xM, yM: component.yM });
    const left = project({ xM: component.xM - component.widthM / 2, yM: component.yM });
    const right = project({ xM: component.xM + component.widthM / 2, yM: component.yM });
    const top = project({ xM: component.xM, yM: component.yM + component.heightM / 2 });
    const bottom = project({ xM: component.xM, yM: component.yM - component.heightM / 2 });
    const projectedWidth = Math.abs(right.x - left.x);
    const projectedHeight = Math.abs(bottom.y - top.y);
    const color = COMPONENT_COLORS[component.kind];
    const abbreviation = component.kind === 'generation-block'
      ? `B${component.blockNumber ?? ''}`
      : component.kind === 'cooling'
        ? 'COOL'
        : component.kind === 'switchyard'
          ? 'SWYD'
          : component.kind === 'ccs-capture'
            ? 'CCS'
            : component.kind === 'ccs-compression'
              ? 'COMP'
              : component.kind === 'natural-gas-route'
                ? 'GAS'
                : component.kind === 'co2-export-route'
                  ? 'CO2'
                  : component.kind === 'access-road' || component.kind === 'road'
                    ? 'ROAD'
                    : component.kind === 'parking'
                      ? 'PARK'
                      : component.kind === 'building'
                        ? 'BLDG'
                        : component.kind === 'data-center'
                          ? 'DC'
                          : component.kind === 'generator'
                            ? 'GEN'
                            : component.kind === 'custom'
                              ? 'CUST'
                              : 'LAY';
    const label = projectedWidth >= 78 && projectedHeight >= 38
      ? `<text x="${compact(labelPoint.x)}" y="${compact(labelPoint.y - 2)}" text-anchor="middle" fill="#ffffff" font-size="13" font-weight="700">${escapeXml(ellipsize(component.label, 23))}</text>
         <text x="${compact(labelPoint.x)}" y="${compact(labelPoint.y + 15)}" text-anchor="middle" fill="#ffffff" font-size="11">${escapeXml(`${decimal(component.areaAcres, 1)} ac`)}</text>`
      : projectedWidth >= 35 && projectedHeight >= 22
        ? `<text x="${compact(labelPoint.x)}" y="${compact(labelPoint.y + 4)}" text-anchor="middle" fill="#ffffff" font-size="10" font-weight="700">${escapeXml(abbreviation)}</text>`
        : '';
    return `<g data-component-id="${escapeXml(component.id)}">
      <path d="${path(component.polygonM.map(project), true)}" fill="${color}" fill-opacity="0.72" stroke="#ffffff" stroke-width="2"/>
      <title>${escapeXml(`${component.label}: ${decimal(component.areaAcres, 1)} acres`)}</title>
      ${label}
    </g>`;
  }).join('');
  return `<g id="generation-layout-plan">
    <rect x="36" y="110" width="748" height="522" rx="8" fill="#ffffff" stroke="#cbd5e1"/>
    <text x="56" y="139" fill="#0f172a" font-size="18" font-weight="700">A-01 · PARCEL TEST FIT</text>
    <text x="764" y="139" text-anchor="end" fill="#475569" font-size="12">${escapeXml(`${decimal(envelopeWidth, 0)} × ${decimal(envelopeHeight, 0)} m envelope`)}</text>
    <g clip-path="url(#generation-layout-plan-clip)">
      <path d="${path(parcel.map(project), true)}" fill="#e2e8f0" fill-opacity="0.75" stroke="#334155" stroke-width="2.5"/>
      <path d="${path(envelope.map(project), true)}" fill="none" stroke="#64748b" stroke-width="1.5" stroke-dasharray="7 5"/>
      ${packingPolygon.length > 0 ? `<path d="${path(packingPolygon.map(project), true)}" fill="none" stroke="#0f766e" stroke-width="1.5" stroke-dasharray="3 4"><title>Envelope after entered edge setback</title></path>` : ''}
      ${exclusions}
      ${components}
    </g>
    ${exclusionSummaryLine(snapshot)}
    <text x="56" y="616" fill="#475569" font-size="12">Rotation ${escapeXml(decimal(snapshot.fit.layout.rotationDeg, 1))}° · edge setback ${escapeXml(decimal(snapshot.fit.layout.edgeSetbackM, 1))} m · component gap ${escapeXml(decimal(snapshot.fit.layout.componentGapM, 1))} m · ${snapshot.fit.fitModelVersion === 'generation-test-fit-v2' || snapshot.fit.fitModelVersion === 'generation-test-fit-v3' ? 'installed reservations + disclosed fallbacks' : 'planning acreage'}</text>
  </g>`;
}

function renderMapFeature(
  feature: GenerationLayoutExhibitOverlayFeature,
  project: (point: { lat: number; lng: number }) => SvgPoint,
): string {
  const coordinates = feature.geometry.coordinates.map(project);
  const color = safeColor(feature);
  const title = escapeXml(`${feature.name} · ${feature.source}${feature.cad ? ` · ${feature.cad.sourceFile} · SHA-256 ${feature.cad.sourceSha256}` : ''}`);
  const geometry = feature.geometry.type === 'point'
    ? coordinates.map(point => `<circle cx="${compact(point.x)}" cy="${compact(point.y)}" r="5" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>`).join('')
    : feature.geometry.type === 'polygon'
      ? `<path d="${path(coordinates, true)}" fill="${color}" fill-opacity="0.17" stroke="${color}" stroke-width="2" vector-effect="non-scaling-stroke"/>`
      : `<path d="${path(coordinates)}" fill="none" stroke="${color}" stroke-width="2.5" vector-effect="non-scaling-stroke"/>`;
  return `<g data-layer-id="${escapeXml(feature.id)}" data-layer-source="${escapeXml(feature.source)}">${geometry}<title>${title}</title></g>`;
}

function renderOverlay(snapshot: GenerationLayoutExhibitSnapshot): string {
  const project = mapProjector(snapshot);
  const parcel = snapshot.fit.parcel?.polygon ?? [];
  const components = snapshot.fit.components.map(component => (
    `<path data-fit-component-id="${escapeXml(component.id)}" d="${path(component.coordinates.map(project), true)}" fill="${COMPONENT_COLORS[component.kind]}" fill-opacity="0.35" stroke="${COMPONENT_COLORS[component.kind]}" stroke-width="1.5"/>`
  )).join('');
  const features = [...snapshot.overlays.features]
    .sort((left, right) => compareText(left.id, right.id))
    .map(feature => renderMapFeature(feature, project))
    .join('');
  const omitted = snapshot.overlays.status !== 'included'
    ? `<g data-overlay-omitted="true">
        <rect x="920" y="322" width="540" height="78" rx="6" fill="#fff7ed" stroke="#c2410c" stroke-width="2"/>
        <text x="1190" y="351" text-anchor="middle" fill="#9a3412" font-size="15" font-weight="700">${snapshot.overlays.status === 'omitted-invalid' ? 'INVALID GEOMETRY · OVERLAY OMITTED IN FULL' : 'OVERLAY GEOMETRY OMITTED IN FULL'}</text>
        <text x="1190" y="376" text-anchor="middle" fill="#7c2d12" font-size="12">${escapeXml(snapshot.overlays.status === 'omitted-invalid'
          ? `${snapshot.overlays.invalidLayerIds.length} invalid layer(s): ${ellipsize(snapshot.overlays.invalidLayerIds.join(', '), 62)}; no prefix retained`
          : `${decimal(snapshot.overlays.serializedBytes, 0)} bytes exceed ${decimal(snapshot.overlays.maxBytes, 0)}-byte ceiling; no prefix retained`)}</text>
      </g>`
    : '';
  return `<g id="generation-layout-overlay">
    <rect x="816" y="110" width="748" height="522" rx="8" fill="#ffffff" stroke="#cbd5e1"/>
    <text x="836" y="139" fill="#0f172a" font-size="18" font-weight="700">FROZEN CAD / SITE OVERLAY</text>
    <text x="1544" y="139" text-anchor="end" fill="#475569" font-size="12">${escapeXml(`${snapshot.overlays.sourceLayerIds.length} visible layers · ${snapshot.overlays.totalVertices} vertices`)}</text>
    <g clip-path="url(#generation-layout-overlay-clip)">
      ${parcel.length >= 3 ? `<path data-map-parcel="selected" d="${path(parcel.map(project), true)}" fill="#e2e8f0" fill-opacity="0.55" stroke="#64748b" stroke-width="2"/>` : ''}
      ${components}
      ${features}
      ${omitted}
    </g>
    <g aria-label="North arrow" transform="translate(1512 173)">
      <text x="0" y="0" text-anchor="middle" fill="#0f172a" font-size="12" font-weight="700">N</text>
      <path d="M0,8 L-8,31 L0,26 L8,31 Z" fill="#0f172a" stroke="#ffffff" stroke-width="1"/>
      <line x1="0" y1="26" x2="0" y2="44" stroke="#0f172a" stroke-width="2"/>
    </g>
    <g aria-label="Exhibit legend">
      <rect x="842" y="535" width="624" height="55" rx="5" fill="#ffffff" fill-opacity="0.9" stroke="#cbd5e1"/>
      <rect x="856" y="546" width="18" height="11" rx="2" fill="${COMPONENT_COLORS['generation-block']}"/><text x="881" y="556" fill="#334155" font-size="9">Generation</text>
      <rect x="956" y="546" width="18" height="11" rx="2" fill="${COMPONENT_COLORS.cooling}"/><text x="981" y="556" fill="#334155" font-size="9">Cooling</text>
      <rect x="1048" y="546" width="18" height="11" rx="2" fill="${COMPONENT_COLORS.switchyard}"/><text x="1073" y="556" fill="#334155" font-size="9">Switchyard</text>
      <rect x="1160" y="546" width="18" height="11" rx="2" fill="${COMPONENT_COLORS['ccs-capture']}"/><text x="1185" y="556" fill="#334155" font-size="9">CCS</text>
      <rect x="856" y="568" width="18" height="11" rx="2" fill="${COMPONENT_COLORS['natural-gas-route']}"/><text x="881" y="578" fill="#334155" font-size="9">Gas route</text>
      <rect x="956" y="568" width="18" height="11" rx="2" fill="${COMPONENT_COLORS['co2-export-route']}"/><text x="981" y="578" fill="#334155" font-size="9">CO2 route</text>
      <rect x="1048" y="568" width="18" height="11" rx="2" fill="${COMPONENT_COLORS['construction-laydown']}"/><text x="1073" y="578" fill="#334155" font-size="9">Laydown</text>
      <line x1="1160" y1="574" x2="1186" y2="574" stroke="#475569" stroke-width="3"/><circle cx="1173" cy="574" r="3" fill="#ffffff" stroke="#475569"/><text x="1193" y="578" fill="#334155" font-size="9">CAD / site layer</text>
    </g>
    <text x="836" y="616" fill="#475569" font-size="12">${snapshot.fit.parcel
      ? 'Parcel-focused frame; off-parcel geometry may be clipped. Every frozen layer path remains in the SVG.'
      : 'No selected parcel: frame uses all frozen overlay extents. Every layer path remains in the SVG.'}</text>
  </g>`;
}

interface ScheduleCell { label: string; value: string; basis: string; status?: 'pass' | 'fail' | 'neutral' }

function renderSchedule(snapshot: GenerationLayoutExhibitSnapshot): string {
  const { engineering } = snapshot;
  const receptor = engineering.nearestReceptor;
  const selectedSizingOption = engineering.sizingStudy?.status === 'available'
    ? engineering.sizingStudy.options.find(
        option => option.id === engineering.sizingStudy?.lowestNameplatePassingReserveOptionId,
      ) ?? null
    : null;
  const equipmentBasis = engineering.equipmentBasis;
  const projectDutyBasis = engineering.projectDutyBasis;
  const cells: ScheduleCell[] = [
    {
      label: projectDutyBasis
        ? 'FLEET / PROJECT DUTY'
        : equipmentBasis ? 'FLEET / EQUIPMENT DUTY' : 'FLEET / GROSS',
      value: equipmentBasis
        ? `${equipmentBasis.selection.manufacturer} ${equipmentBasis.selection.model} · ${engineering.fleet.generationBlockCount} block(s)`
        : `${engineering.fleet.generationBlockCount} block(s) · ${decimal(engineering.fleet.siteGrossMW, 1)} MW site gross`,
      basis: projectDutyBasis
        ? `${projectDutyBasis.alignmentStatus.toUpperCase()} · ${projectDutyBasis.completeness.suppliedCount}/${projectDutyBasis.completeness.totalCount} evidence categories · ${decimal(engineering.fleet.siteGrossMW, 1)} MW site gross`
        : equipmentBasis
        ? `${decimal(equipmentBasis.selection.publishedRating.outputMWPerBlock, 1)} MW ${equipmentBasis.selection.publishedRating.ratingScope.replaceAll('-', ' ')} · ${equipmentBasis.alignmentStatus.toUpperCase()} · ${decimal(engineering.fleet.siteGrossMW, 1)} MW site gross`
        : `${decimal(engineering.fleet.isoGrossNameplateMW, 1)} MW ISO nameplate`,
    },
    {
      label: 'NORMAL / N-1',
      value: `${decimal(engineering.capacity.normalNetMW, 1)} / ${decimal(engineering.capacity.nMinusOneNetMW, 1)} MW net`,
      basis: `${decimal(engineering.capacity.nMinusOneMarginMW, 1)} MW N-1 margin`,
      status: engineering.capacity.nMinusOneVerdict === 'pass' ? 'pass' : engineering.capacity.nMinusOneVerdict === 'fail' ? 'fail' : 'neutral',
    },
    {
      label: 'N-1 + RESERVE · CONFIGURED',
      value: `Configured ${signedDecimal(engineering.capacity.nMinusOneReserveMarginMW, 1)} MW · ${engineering.capacity.nMinusOneReserveVerdict.toUpperCase()}`,
      basis: selectedSizingOption
        ? `Req ${decimal(engineering.capacity.reserveRequiredMW, 1)} MW · screen ${selectedSizingOption.blockCount} × ${decimal(selectedSizingOption.isoGrossMWPerBlock, 0)} MW · ${signedDecimal(selectedSizingOption.nMinusOneReserveMarginMW, 1)} MW · CCS ${selectedSizingOption.ccsEnabled ? 'ON' : 'OFF'}`
        : engineering.sizingStudy
          ? `Req ${decimal(engineering.capacity.reserveRequiredMW, 1)} MW · capacity screen unavailable`
          : `Req ${decimal(engineering.capacity.reserveRequiredMW, 1)} MW · alternatives not frozen`,
      status: engineering.capacity.nMinusOneReserveVerdict === 'pass' ? 'pass' : engineering.capacity.nMinusOneReserveVerdict === 'fail' ? 'fail' : 'neutral',
    },
    {
      label: 'NATURAL GAS',
      value: `${decimal(engineering.gas.peakDthPerDay, 0)} Dth/day`,
      basis: projectDutyBasis?.basis.fuelGas
        ? `${decimal(projectDutyBasis.basis.fuelGas.minimumRequired.valueKPa, 0)} kPa ${projectDutyBasis.basis.fuelGas.minimumRequired.reference} minimum · ${projectDutyBasis.gasPressureCheck.verdict.toUpperCase()}`
        : `${decimal(engineering.gas.peakMmbtuPerHour, 0)} MMBtu/h peak`,
    },
    {
      label: 'GENERATION + CCS WATER',
      value: `${decimal(engineering.water.generationConsumptionGalPerDay + engineering.water.ccsConsumptionGalPerDay, 0)} gal/day consumed`,
      basis: `${decimal(engineering.water.generationWithdrawalGalPerDay + engineering.water.ccsWithdrawalGalPerDay, 0)} gal/day withdrawn`,
    },
    {
      label: 'ON-SITE CO2 (UNCONTROLLED / CAPTURED / RESIDUAL)',
      value: `${decimal(engineering.emissions.uncontrolledCo2TonnesPerYear, 0)} / ${decimal(engineering.emissions.capturedCo2TonnesPerYear, 0)} / ${decimal(engineering.emissions.residualCo2TonnesPerYear, 0)} t/y`,
      basis: 'Generation only; purchased-grid and unmodeled standby excluded',
    },
    {
      label: projectDutyBasis ? 'RESERVATIONS / PARCEL FIT' : 'FOOTPRINT / PARCEL FIT',
      value: projectDutyBasis
        ? `${decimal(projectDutyBasis.spatialSummary.totalReservedAcres, 1)} reserved ac · ${snapshot.fit.status.toUpperCase()}`
        : `${decimal(engineering.footprint.totalAcres, 1)} ac · ${snapshot.fit.status.toUpperCase()}`,
      basis: projectDutyBasis
        ? `${decimal(projectDutyBasis.spatialSummary.permanentEquipmentAcres, 1)} permanent · ${decimal(projectDutyBasis.spatialSummary.routeReservationAcres, 1)} route · ${decimal(projectDutyBasis.spatialSummary.constructionLaydownAcres, 1)} laydown ac`
        : `${decimal(snapshot.fit.minimumParcelEdgeClearanceM, 1)} m minimum parcel-edge clearance`,
      status: snapshot.fit.status === 'fits' ? 'pass' : snapshot.fit.status === 'partial' ? 'fail' : 'neutral',
    },
    {
      label: 'NOISE / LIGHTING',
      value: receptor
        ? `${ellipsize(receptor.receptorName, 24)} · ${decimal(receptor.predictedDbA, 1)} dBA`
        : 'Nearest receptor not established',
      basis: `${decimal(engineering.lighting.installedPowerKW, 1)} kW installed lighting · ${engineering.lighting.planningFlags.length} mitigation flag(s)`,
      status: receptor?.verdict === 'pass' ? 'pass' : receptor?.verdict === 'exceedance' ? 'fail' : 'neutral',
    },
  ];
  const cellWidth = 374;
  const cellHeight = 103;
  const startX = 36;
  const startY = 681;
  const rendered = cells.map((cell, index) => {
    const column = index % 4;
    const row = Math.floor(index / 4);
    const x = startX + column * (cellWidth + 8);
    const y = startY + row * (cellHeight + 8);
    const statusColor = cell.status === 'pass' ? '#15803d' : cell.status === 'fail' ? '#b91c1c' : '#64748b';
    return `<g data-schedule-index="${index}">
      <rect x="${x}" y="${y}" width="${cellWidth}" height="${cellHeight}" rx="6" fill="#ffffff" stroke="#cbd5e1"/>
      <rect x="${x}" y="${y}" width="5" height="${cellHeight}" rx="2" fill="${statusColor}"/>
      <text x="${x + 18}" y="${y + 25}" fill="#475569" font-size="10" font-weight="700" letter-spacing="0.7">${escapeXml(cell.label)}</text>
      <text x="${x + 18}" y="${y + 53}" fill="#0f172a" font-size="15" font-weight="700">${escapeXml(ellipsize(cell.value, 47))}</text>
      <text x="${x + 18}" y="${y + 78}" fill="#64748b" font-size="11">${escapeXml(ellipsize(cell.basis, 58))}</text>
    </g>`;
  }).join('');
  return `<g id="generation-layout-engineering-schedule">
    <text x="36" y="662" fill="#0f172a" font-size="17" font-weight="700">ENGINEERING SCHEDULE · FROZEN SCREENING BASIS</text>
    <text x="1564" y="662" text-anchor="end" fill="#475569" font-size="11">${escapeXml(projectDutyBasis
      ? `${projectDutyBasis.completeness.suppliedCount}/${projectDutyBasis.completeness.totalCount} project evidence categories · ${engineering.gaps.length} gap(s) · ${engineering.warnings.length + snapshot.warnings.length} warning(s)`
      : `${engineering.gaps.length} gap(s) · ${engineering.warnings.length + snapshot.warnings.length} warning(s) · ${engineering.noiseCaveats.length} acoustic caveat(s)`)}</text>
    ${rendered}
  </g>`;
}

/**
 * Deterministic, browser/server-safe A-01 exhibit. It emits plain SVG only:
 * no live store reads, browser APIs, external styles, random ids or timestamps.
 */
export function renderGenerationLayoutExhibitSvg(
  snapshot: GenerationLayoutExhibitSnapshot,
): string {
  snapshot = requireValidGenerationLayoutExhibitSnapshot(snapshot);
  const cad = snapshot.overlays.cadSources[0];
  const provenance = cad
    ? `${cad.sourceFile} · SHA-256 ${cad.sourceSha256}`
    : 'No verified DWG source frozen';
  const authority = snapshot.provenance.frozenAuthority;
  const authorityLine = authority
    ? `Scenario ${authority.scenarioId} · layout ${authority.layoutPublicationId} v${authority.layoutVersion} · boundary ${authority.boundaryHash}`
    : `Scenario ${snapshot.scenario.id} · result ${snapshot.scenario.resultHash} · layers revision ${snapshot.provenance.siteLayersRevision ?? 'not recorded'}`;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${GENERATION_LAYOUT_EXHIBIT_SVG_WIDTH}" height="${GENERATION_LAYOUT_EXHIBIT_SVG_HEIGHT}" viewBox="${GENERATION_LAYOUT_EXHIBIT_SVG_VIEW_BOX}" style="display:block;width:100%;height:auto;background:#f8fafc" role="img" aria-labelledby="generation-layout-title generation-layout-description" data-export-id="${GENERATION_LAYOUT_EXHIBIT_EXPORT_ID}">
  <title id="generation-layout-title">${escapeXml(`On-Site Generation Test Fit — ${snapshot.scenario.name}`)}</title>
  <desc id="generation-layout-description">Frozen deterministic parcel fit, CAD and site overlay, engineering impacts, and provenance.</desc>
  <defs>
    <clipPath id="generation-layout-plan-clip"><rect x="56" y="154" width="708" height="442"/></clipPath>
    <clipPath id="generation-layout-overlay-clip"><rect x="836" y="154" width="708" height="442"/></clipPath>
  </defs>
  <rect width="1600" height="1000" fill="#f8fafc"/>
  <rect width="1600" height="84" fill="#0f172a"/>
  <text x="36" y="36" fill="#f8fafc" font-family="Arial,Helvetica,sans-serif" font-size="12" font-weight="700" letter-spacing="1.7">ON-SITE GENERATION TEST FIT EXHIBIT</text>
  <text x="36" y="67" fill="#ffffff" font-family="Arial,Helvetica,sans-serif" font-size="24" font-weight="700">${escapeXml(ellipsize(snapshot.scenario.name, 76))}</text>
  <text x="1564" y="36" text-anchor="end" fill="#cbd5e1" font-family="Arial,Helvetica,sans-serif" font-size="11">A-01 · SCREENING / NOT FOR CONSTRUCTION</text>
  <text x="1564" y="64" text-anchor="end" fill="#ffffff" font-family="Arial,Helvetica,sans-serif" font-size="13" font-weight="700">${escapeXml(`${snapshot.fit.status.toUpperCase()} · ${snapshot.engineering.capacity.nMinusOneVerdict.toUpperCase()} N-1`)}</text>
  <g font-family="Arial,Helvetica,sans-serif">
    ${renderPlan(snapshot)}
    ${renderOverlay(snapshot)}
    ${renderSchedule(snapshot)}
  </g>
  <line x1="36" y1="922" x2="1564" y2="922" stroke="#cbd5e1"/>
  <text x="36" y="946" fill="#334155" font-family="Arial,Helvetica,sans-serif" font-size="11">${escapeXml(ellipsize(provenance, 150))}</text>
  <text x="36" y="969" fill="#64748b" font-family="Arial,Helvetica,sans-serif" font-size="10">${escapeXml(ellipsize(authorityLine, 190))}</text>
  <text x="1564" y="946" text-anchor="end" fill="#334155" font-family="Arial,Helvetica,sans-serif" font-size="11">${escapeXml(`Captured ${snapshot.capturedAt}`)}</text>
  <text x="1564" y="969" text-anchor="end" fill="#64748b" font-family="Arial,Helvetica,sans-serif" font-size="10">${escapeXml(`Exhibit SHA-256 ${snapshot.contentHash}`)}</text>
</svg>`;
}
